// Worker lifecycle mutations, shared by the Workers table and the worker detail
// page so both surfaces behave identically.
//
// Stop and start run as Operations (they drain, render and health-wait), so the
// caller gets an operationId to follow. Redeploy does too — it reruns the whole
// remote pipeline. Delete is synchronous.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, unwrap } from "../api.ts";
import type { WorkerRow } from "./types.ts";

interface OperationStarted {
	operationId: string;
}

interface DeleteResult {
	teardown?: {
		containerRemoved: boolean;
		volumeRemoved: boolean;
		warnings: string[];
	};
}

/**
 * One key for the inventory, one for the cluster's dirty flag.
 *
 * Starting or stopping a worker used to invalidate only `["cluster","fleet"]`,
 * which is why the Workers page went stale after a lifecycle action: it read a
 * different key over the same rows. There is one list endpoint now, so there is
 * one key.
 */
function useInvalidateWorkers() {
	const queryClient = useQueryClient();
	return () => {
		queryClient.invalidateQueries({ queryKey: ["cluster"] });
		queryClient.invalidateQueries({ queryKey: ["workers"] });
	};
}

export function useWorkerStop(opts?: {
	onStarted?: (operationId: string) => void;
}) {
	const invalidate = useInvalidateWorkers();
	return useMutation({
		mutationFn: async (hostname: string) =>
			(await unwrap(
				api.api.workers({ hostname }).stop.post(),
			)) as OperationStarted,
		onSuccess: ({ operationId }) => {
			invalidate();
			opts?.onStarted?.(operationId);
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Stop failed"),
	});
}

export function useWorkerStart(opts?: {
	onStarted?: (operationId: string) => void;
}) {
	const invalidate = useInvalidateWorkers();
	return useMutation({
		mutationFn: async (hostname: string) =>
			(await unwrap(
				api.api.workers({ hostname }).start.post(),
			)) as OperationStarted,
		onSuccess: ({ operationId }) => {
			invalidate();
			opts?.onStarted?.(operationId);
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Start failed"),
	});
}

/**
 * Whether this manager can open the worker's own machine over SSH.
 *
 * The two grounds are exactly the ones `openRemoteAccess()` refuses on in
 * lib/worker-remote-access.ts: a worker running as a container here has no host
 * to reach, and a worker with no saved key leaves the manager without a
 * credential for one. Gating the buttons on the same two facts is what keeps the
 * console from offering an action the API can only turn down.
 *
 * `containerName` and not `placement`: placement is operator-editable metadata
 * about where a worker sits, while a container name is the control fact the
 * server actually branches on.
 */
export function canReachRemoteHost(
	worker: Pick<WorkerRow, "containerName" | "sshKeyId">,
): boolean {
	return worker.containerName === null && worker.sshKeyId !== null;
}

/**
 * Rerun the remote bootstrap against a worker's saved key.
 *
 * Also the finish for a half-failed deploy: the rollback leaves that row
 * registered and out of the pool on purpose, and the pipeline converges on it
 * rather than adding a second worker.
 */
export function useWorkerRedeploy(opts?: {
	onStarted?: (operationId: string) => void;
}) {
	const invalidate = useInvalidateWorkers();
	return useMutation({
		mutationFn: async (hostname: string) =>
			(await unwrap(
				api.api.workers({ hostname }).redeploy.post(),
			)) as OperationStarted,
		onSuccess: ({ operationId }) => {
			invalidate();
			opts?.onStarted?.(operationId);
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Redeploy failed"),
	});
}

export interface DeleteWorkerVars {
	hostname: string;
	removeContainer: boolean;
	removeVolume: boolean;
	/** SSH in and take the stack off the far host, for a remote worker. */
	uninstall: boolean;
}

export function useWorkerDelete(opts?: { onDeleted?: () => void }) {
	const invalidate = useInvalidateWorkers();
	return useMutation({
		mutationFn: async (vars: DeleteWorkerVars) =>
			(await unwrap(
				api.api.workers({ hostname: vars.hostname }).delete(undefined, {
					query: {
						removeContainer: String(vars.removeContainer),
						removeVolume: String(vars.removeVolume),
						uninstall: String(vars.uninstall),
					},
				}),
			)) as DeleteResult,
		onSuccess: (res, vars) => {
			// Teardown is best-effort so the row always goes away; surface any
			// leftover docker resources instead of failing silently.
			const warnings = res.teardown?.warnings ?? [];
			if (warnings.length > 0) {
				toast.warning(`Removed ${vars.hostname}, with warnings`, {
					description: warnings.join("; "),
				});
			} else {
				toast.success(`Removed ${vars.hostname}`);
			}
			invalidate();
			opts?.onDeleted?.();
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Delete failed"),
	});
}
